import { useDeferredValue, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { SearchOutlined } from '@mui/icons-material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { AlertSnippetDto, CustomerDto, CustomerType } from '../../../interfaces/IEvent'
import { CustomerNotificationRow, RecipientType, computeNotificationStatus } from './computeNotificationStatus'
import StyledToggleButtonGroup from '../../../components/InputFields/StyledToggleButtonGroup'

type NotificationStatusKey = 'notified' | 'pending' | 'draft' | 'none'

const statusLabel: Record<NotificationStatusKey, string> = {
  notified: 'Varslet',
  pending: 'Ventende',
  draft: 'Utkast',
  none: 'Ikke varslet',
}

const statusMatches = (row: CustomerNotificationRow, status: NotificationStatusKey): boolean => {
  switch (status) {
    case 'notified':
      return row.isNotified
    case 'pending':
      return row.pendingInAlerts.length > 0
    case 'draft':
      return row.draftInAlerts.length > 0
    case 'none':
      return !row.isNotified && row.pendingInAlerts.length === 0 && row.draftInAlerts.length === 0
  }
}

interface CustomerNotificationStatusProps {
  affectedCustomers: CustomerDto[]
  alerts: AlertSnippetDto[]
  affectedPhoneNumbers?: string[]
  affectedEmailAddresses?: string[]
}

const customerTypeLabel: Record<CustomerType, string> = {
  [CustomerType.Private]: 'Privat',
  [CustomerType.Company]: 'Bedrift',
  [CustomerType.CompanyWithSla]: 'SLA',
  [CustomerType.Undefined]: 'Ukjent',
}

const recipientTypeLabel: Record<RecipientType, string> = {
  customer: '',
  phone: 'Telefon',
  email: 'Epost',
}

function getTypeLabel(row: CustomerNotificationRow): string {
  if (row.recipientType !== 'customer') return recipientTypeLabel[row.recipientType]
  return customerTypeLabel[row.customerType] ?? row.customerType
}

const columns: GridColDef<CustomerNotificationRow>[] = [
  { field: 'customerName', headerName: 'Kundenavn', flex: 1, minWidth: 200 },
  {
    field: 'recipientType',
    headerName: 'Type',
    width: 120,
    renderCell: (params) => (
      <Chip label={getTypeLabel(params.row)} size="small" variant="outlined" />
    ),
  },
  {
    field: 'isNotified',
    headerName: 'Varslet',
    width: 100,
    renderCell: (params) =>
      params.value ? (
        <Chip label="Ja" size="small" color="success" />
      ) : (
        <Chip label="Nei" size="small" variant="outlined" />
      ),
  },
  {
    field: 'notifiedInAlerts',
    headerName: 'Varslet i',
    flex: 1,
    minWidth: 150,
    valueGetter: (value: string[]) => value.join(', '),
  },
  {
    field: 'pendingInAlerts',
    headerName: 'Ventende',
    flex: 1,
    minWidth: 150,
    valueGetter: (value: string[]) => value.join(', '),
  },
  {
    field: 'draftInAlerts',
    headerName: 'Utkast',
    flex: 1,
    minWidth: 150,
    valueGetter: (value: string[]) => value.join(', '),
  },
]

const statusKeys: NotificationStatusKey[] = ['none', 'notified', 'pending', 'draft']
const recipientTypeKeys: RecipientType[] = ['customer', 'phone', 'email']
const customerTypeKeys: CustomerType[] = [
  CustomerType.Private,
  CustomerType.Company,
  CustomerType.CompanyWithSla,
]

function CustomerNotificationStatus({ affectedCustomers, alerts, affectedPhoneNumbers, affectedEmailAddresses }: CustomerNotificationStatusProps) {
  const rows = useMemo(
    () => computeNotificationStatus(affectedCustomers, alerts, affectedPhoneNumbers ?? [], affectedEmailAddresses ?? []),
    [affectedCustomers, alerts, affectedPhoneNumbers, affectedEmailAddresses],
  )

  const alertOptions = useMemo(() => {
    const baseLabelCounts = new Map<string, number>()
    alerts.forEach((a) => {
      const base = a.description || a.id
      baseLabelCounts.set(base, (baseLabelCounts.get(base) ?? 0) + 1)
    })
    return alerts.map((a) => {
      const base = a.description || a.id
      const needsDisambiguation = (baseLabelCounts.get(base) ?? 0) > 1
      let suffix = ''
      if (needsDisambiguation && a.sendOn) {
        const d = new Date(a.sendOn)
        if (!isNaN(d.getTime())) {
          suffix = d.toLocaleString('nb-NO', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        }
      }
      const label = suffix ? `${base} (${suffix})` : base
      return { id: a.id, label }
    })
  }, [alerts])

  const [selectedAlertIds, setSelectedAlertIds] = useState<string[]>([])
  const [alertFilterMode, setAlertFilterMode] = useState<'included' | 'notIncluded'>('included')
  const [selectedStatus, setSelectedStatus] = useState<NotificationStatusKey | null>(null)
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const deferredSearch = useDeferredValue(searchTerm).trim().toLowerCase()

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (deferredSearch) {
          const matches =
            row.customerName?.toLowerCase().includes(deferredSearch) ||
            row.customerNumber?.toLowerCase().includes(deferredSearch)
          if (!matches) return false
        }
        if (selectedAlertIds.length > 0) {
          const rowAlertIds = [...row.notifiedAlertIds, ...row.pendingAlertIds, ...row.draftAlertIds]
          const isInSelectedAlerts = selectedAlertIds.some((id) => rowAlertIds.includes(id))
          if (alertFilterMode === 'included' ? !isInSelectedAlerts : isInSelectedAlerts) return false
        }
        if (selectedStatus && !statusMatches(row, selectedStatus)) return false
        if (selectedTypes.length > 0) {
          if (row.recipientType === 'customer') {
            if (!selectedTypes.includes(row.customerType)) return false
          } else {
            if (!selectedTypes.includes(row.recipientType)) return false
          }
        }
        return true
      }),
    [rows, deferredSearch, selectedAlertIds, alertFilterMode, selectedStatus, selectedTypes],
  )

  const hasActiveFilter =
    selectedAlertIds.length > 0 || selectedStatus !== null || selectedTypes.length > 0 || searchTerm.length > 0

  const handleAlertChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    setSelectedAlertIds(typeof value === 'string' ? value.split(',') : value)
  }

  const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    setSelectedTypes(typeof value === 'string' ? value.split(',') : value)
  }

  const resetFilters = () => {
    setSelectedAlertIds([])
    setAlertFilterMode('included')
    setSelectedStatus(null)
    setSelectedTypes([])
    setSearchTerm('')
  }

  const statusCounts = useMemo(() => {
    const counts: Record<NotificationStatusKey, number> = { notified: 0, pending: 0, draft: 0, none: 0 }
    rows.forEach((row) => {
      statusKeys.forEach((key) => { if (statusMatches(row, key)) counts[key]++ })
    })
    return counts
  }, [rows])

  const handleQuickFilter = (status: NotificationStatusKey) => {
    setSelectedStatus(selectedStatus === status ? null : status)
  }

  return (
    <Box>
      <Box sx={{ mt: 1, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {statusKeys.map((key) => {
          const isActive = selectedStatus === key
          return (
            <Chip
              key={key}
              label={`${statusLabel[key]} (${statusCounts[key]})`}
              size="small"
              variant={isActive ? 'filled' : 'outlined'}
              color={isActive ? 'primary' : 'default'}
              onClick={() => handleQuickFilter(key)}
            />
          )
        })}
      </Box>
      <Box sx={{ mt: 1, mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Søk etter navn, kundenummer, telefon, epost..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Varsel</InputLabel>
          <Select
            multiple
            value={selectedAlertIds}
            onChange={handleAlertChange}
            input={<OutlinedInput label="Varsel" />}
            renderValue={(selected) => {
              const ids = selected as string[]
              const visible = ids.slice(0, 2)
              const overflow = ids.length - visible.length
              return (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
                  {visible.map((id) => {
                    const label = alertOptions.find((o) => o.id === id)?.label ?? id
                    return <Chip key={id} size="small" label={label} sx={{ maxWidth: 140 }} />
                  })}
                  {overflow > 0 && <Chip size="small" label={`+${overflow}`} />}
                </Box>
              )
            }}
          >
            {alertOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={selectedAlertIds.includes(option.id)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedAlertIds.length > 0 && (
          <StyledToggleButtonGroup
            value={alertFilterMode}
            onChange={setAlertFilterMode}
            options={[
              { value: 'included' as const, label: 'Inkludert' },
              { value: 'notIncluded' as const, label: 'Ikke inkludert' },
            ]}
          />
        )}

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            multiple
            value={selectedTypes}
            onChange={handleTypeChange}
            input={<OutlinedInput label="Type" />}
            renderValue={(selected) => {
              const visible = selected.slice(0, 2)
              const overflow = selected.length - visible.length
              const labelFor = (t: string) => customerTypeLabel[t as CustomerType] ?? recipientTypeLabel[t as RecipientType] ?? t
              return (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
                  {visible.map((t) => (
                    <Chip key={t} size="small" label={labelFor(t)} />
                  ))}
                  {overflow > 0 && <Chip size="small" label={`+${overflow}`} />}
                </Box>
              )
            }}
          >
            {customerTypeKeys.map((key) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={selectedTypes.includes(key)} />
                <ListItemText primary={customerTypeLabel[key]} />
              </MenuItem>
            ))}
            {recipientTypeKeys.filter((k) => k !== 'customer').map((key) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={selectedTypes.includes(key)} />
                <ListItemText primary={recipientTypeLabel[key]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasActiveFilter && (
          <Button onClick={resetFilters} size="small">
            Nullstill filtre
          </Button>
        )}
      </Box>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.customerNumber ?? `unknown-${row.customerName}`}
        hideFooterSelectedRowCount
        showCellVerticalBorder
        showColumnVerticalBorder
        disableColumnFilter
        localeText={{ noRowsLabel: 'Ingen kunder funnet' }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
          sorting: {
            sortModel: [{ field: 'customerName', sort: 'asc' }],
          },
        }}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  )
}

export default CustomerNotificationStatus
